import networkApi from '@lib/networkApi';
import {
  type WifiConnectionResult,
  type WifiDisconnectionResult,
  type WifiNetwork,
  type WifiNetworksResult,
} from '@lib/networkApiTypes';

function networkApiRejection (reject: (errorMessage: string) => void, context: string) {
  return (_xhr: XMLHttpRequest, status: string, error: string) => { reject(`Error while ${context} with status: "${status}" and error: "${error}".`); };
}

async function fetchNetworks (): Promise<WifiNetworksResult> {
  return await new Promise<WifiNetworksResult>(
    (resolve, reject) => { networkApi.wifi.networks(resolve, networkApiRejection(reject, 'getting wifi networks')); },
  );
}

/**
 * Polls the specified network at a set interval.
 * Polling will continue on error.
 * If you want to stop polling when error occur, provide an error callback that calls the cancellation callback.
 *
 * @param networkName The network to poll.
 * @param intervalMs The interval in milliseconds between requests.
 * @param onSuccess A callback for when we get a polling result.
 * @param [onError] A callback for when a request fails.
 * @returns A function to stop polling.
 */
function pollNetworks (
  intervalMs: number,
  onSuccess: (networkResult: WifiNetworksResult) => void,
  onError?: (xhr: XMLHttpRequest, status: string, error: string) => void,
): () => void {
  let pollNetworksCancellation: (() => void) | undefined;
  let requestTimeout: ReturnType<typeof setTimeout>;

  function poll (): void {
    pollNetworksCancellation = networkApi.wifi.networks(
      (networkResultResponse) => {
        onSuccess(networkResultResponse);
        requestTimeout = setTimeout(poll, intervalMs);
      },
      (xhr, status, error) => {
        onError?.(xhr, status, error);
        requestTimeout = setTimeout(poll, intervalMs);
      },
    );
  }

  poll();

  return () => {
    pollNetworksCancellation?.();
    clearTimeout(requestTimeout);
  };
}

async function connect (wifiNetwork: WifiNetwork, password: string): Promise<void> {
  await new Promise<WifiConnectionResult | undefined>(
    (resolve, reject) => { networkApi.wifi.connect(wifiNetwork.ssid, password, resolve, networkApiRejection(reject, `connecting to wifi "${wifiNetwork.ssid}"`)); },
  )
    .then(wifiConnectionResult => {
      if (wifiConnectionResult === undefined) {
        throw new Error(`No wifi.connect result received while trying to connect to wifi "${wifiNetwork.ssid}".`);
      }

      if (wifiConnectionResult.error !== undefined) {
        throw new Error(`Error connecting to wifi: ${JSON.stringify(wifiConnectionResult.error)}`);
      }
    });
}

async function disconnect (): Promise<void> {
  await new Promise<WifiDisconnectionResult | undefined>(
    (resolve, reject) => { networkApi.wifi.disconnect(resolve, networkApiRejection(reject, 'disconnecting from wifi')); },
  )
    .then(wifiDisconnectionResult => {
      if (wifiDisconnectionResult === undefined) {
        throw new Error('No wifi.disconnect result received while trying to disconnect from wifi.');
      }

      if (wifiDisconnectionResult.error !== undefined) {
        throw new Error(`Error disconnecting from wifi: ${JSON.stringify(wifiDisconnectionResult.error)}`);
      }
    });
}

export const WifiApi = {
  fetchNetworks,
  pollNetworks,
  connect,
  disconnect,
};
